import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createContext } from 'react';

import { CurrencyTypeEnum, VolSurfaceToken } from '../model/currency_type.enum';
import { SnapshotTypeEnum } from '../model/snapshot_type';
import Utils from '../utils/immersive_utils';

import { SnapshotRequest } from './model/snapshotData';
import { OverviewContext } from './overview-provider';
import { WebSocketContext } from './websocket-context/websocket-provider';
import usePersistentState from './use-persistent-state';
import usePersistentRef from './use-persistent-ref';
import { useSmartInactivityWake } from '../hooks/use-smart-inactivity-wake';

interface IParamsProvider {
  children: ReactNode;
  providerKey?: string;
  tokenKey?: VolSurfaceToken;
}

interface IProvider {
  socketEvent: any;
  loading: boolean;
  setLoading: (value: boolean) => void;
  currentSnapshotID: MutableRefObject<string>;
  getData: (timestamp?: string) => void;
  unsubscribe: (currency: VolSurfaceToken) => void;
  type: MutableRefObject<SnapshotTypeEnum>;
  snapshotType: SnapshotTypeEnum;
  setSnapshotType: (type: SnapshotTypeEnum) => void;
  isLiveData: boolean;
  isConnected: boolean;
  handleClickPointVolSeries: (timestamp: any) => void;
  currentVols: string[];
  token: VolSurfaceToken;
  setToken: (token: VolSurfaceToken) => void;
}

const SNAPSHOT_DATA = 'snapshot_data';
const HISTORIC_DATA = 'historic_data';

export const SnapshotContext = createContext<IProvider>({} as IProvider);

export const SnapshotProvider = (props: IParamsProvider) => {
  const STATE_PATH = ['SNAPSHOT', props.providerKey ?? 'default', props.tokenKey ?? ''];

  const { setTimeStamp } = useContext(OverviewContext);
  const [token, setToken] = usePersistentState<VolSurfaceToken>(
    STATE_PATH,
    'token',
    props.tokenKey ?? CurrencyTypeEnum.BITCOIN,
  );

  const selectedToken = useMemo<VolSurfaceToken>(
    () => props.tokenKey ?? token,
    [props.tokenKey, token],
  );

  const {
    subscribeToChannel,
    unsubscribeFromChannel: unsubscribeFromChannel,
    isConnected,
    sendMessage,
  } = useContext(WebSocketContext);
  const [socketEvent, setSocketEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [snapshotType, setSnapshotType] = usePersistentState<SnapshotTypeEnum>(
    STATE_PATH,
    'snapshotType',
    SnapshotTypeEnum.EE,
  );

  const type = usePersistentRef(STATE_PATH, 'snapshotType', SnapshotTypeEnum.EE);
  const currentSnapshotID = useRef<string>(SNAPSHOT_DATA);
  const [isLiveData, setLiveData] = useState<boolean>(currentSnapshotID.current === SNAPSHOT_DATA);
  const lastSubscriptionId = useRef<string | null>(null);
  const lastRefreshAtRef = useRef<number>(0);

  const currentVols = useMemo(() => {
    if (socketEvent && socketEvent.params && socketEvent.params.data) {
      const { data } = socketEvent.params;

      const tenors = Object.keys(data.tenors || []);

      const newArr: string[] = [];

      tenors.forEach((tenor) => {
        const vols = Object.keys(data.tenors[tenor] || []);

        vols.forEach((vol) => {
          newArr.push(`${vol.toUpperCase()}_${tenor.toUpperCase()}`);
        });
      });

      return newArr;
    }

    return [];
  }, [socketEvent]);

  const generateCompositeId = (id: string) => {
    return Utils.generateCompositeIdWithPrefix(id, selectedToken, props.providerKey);
  };

  const handleResponse = (response: any) => {
    try {
      if (!response) return;
      if (response.result && typeof response.result === 'number') return;

      const timestamp = response.params?.data?.timestamp ?? response.result?.timestamp;
      const snapshot_time = response.params?.data?.snapshot_time ?? response.result?.snapshot_time;

      if (timestamp || snapshot_time) {
        const raw = (timestamp ?? snapshot_time) as string;
        const parsed = new Date(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
        const dataTimeMs = parsed.getTime();
        const nowMs = Date.now();

        if (!isNaN(dataTimeMs)) {
          const diffMs = nowMs - dataTimeMs;
          const isLive = diffMs >= 0 && diffMs <= 3 * 60 * 1000;
          setLiveData(isLive);
        }
      }

      setSocketEvent({ ...response });
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const { label, currentTimeStamp } = useContext(OverviewContext);

  const getData = useCallback(
    (timestamp?: string) => {
      if (lastSubscriptionId.current) {
        unsubscribeFromChannel(lastSubscriptionId.current);
        lastSubscriptionId.current = null;
      }

      const id = `${generateCompositeId(currentSnapshotID.current)}-${type.current}`;

      const req = new SnapshotRequest(
        id,
        selectedToken,
        type.current,
        timestamp || currentTimeStamp,
      );

      subscribeToChannel(id, handleResponse);
      lastSubscriptionId.current = id;
      sendMessage(JSON.stringify(req));
    },
    [
      currentSnapshotID,
      currentTimeStamp,
      sendMessage,
      type,
      selectedToken,
      subscribeToChannel,
      unsubscribeFromChannel,
    ],
  );

  const refreshLiveIfStale = useCallback(
    (force: boolean = false) => {
      if (currentSnapshotID.current !== SNAPSHOT_DATA) return;

      const now = Date.now();
      if (now - lastRefreshAtRef.current < 10_000) return;

      const rawTs =
        socketEvent?.params?.data?.timestamp ||
        socketEvent?.result?.timestamp ||
        socketEvent?.params?.data?.snapshot_time ||
        socketEvent?.result?.snapshot_time;

      let shouldRefresh = force;
      if (!rawTs && !force) {
        shouldRefresh = true;
      } else if (rawTs && !force) {
        const parsed = new Date(
          (rawTs as string).includes('T')
            ? (rawTs as string)
            : `${(rawTs as string).replace(' ', 'T')}Z`,
        );
        const dataTimeMs = parsed.getTime();
        if (isNaN(dataTimeMs)) {
          shouldRefresh = true;
        } else {
          const diffMs = Date.now() - dataTimeMs;
          shouldRefresh = diffMs > 3 * 60 * 1000 || diffMs < 0;
        }
      }

      if (shouldRefresh) {
        lastRefreshAtRef.current = now;
        setLoading(true);
        if (lastSubscriptionId.current) {
          unsubscribeFromChannel(lastSubscriptionId.current);
          lastSubscriptionId.current = null;
        }
        const nowIso = new Date().toISOString();
        setTimeStamp(nowIso);
        getData(nowIso);
      }
    },
    [socketEvent, getData, setLoading, setTimeStamp, unsubscribeFromChannel],
  );

  const handleClickPointVolSeries = useCallback(
    (timestamp: any) => {
      setLoading(true);
      const date = new Date(timestamp * 1000);
      setTimeStamp(date.toISOString());
      currentSnapshotID.current = HISTORIC_DATA;
      if (lastSubscriptionId.current) {
        unsubscribeFromChannel(lastSubscriptionId.current);
        lastSubscriptionId.current = null;
      }
      getData(date.toISOString());
    },
    [getData, setLoading, setTimeStamp, unsubscribeFromChannel],
  );

  const handleChangeToken = useCallback(() => {
    if (!isConnected) return;

    if (lastSubscriptionId.current) {
      unsubscribeFromChannel(lastSubscriptionId.current);
      lastSubscriptionId.current = null;
    }

    if (currentSnapshotID.current === HISTORIC_DATA) {
      getData(currentTimeStamp);
    } else {
      currentSnapshotID.current = SNAPSHOT_DATA;
      const nowIso = new Date().toISOString();
      setTimeStamp(nowIso);
      getData(nowIso);
    }
  }, [getData, isConnected, currentTimeStamp, setTimeStamp, unsubscribeFromChannel]);

  useEffect(() => {
    if (isConnected) {
      handleChangeToken();
    }

    return () => {
      if (lastSubscriptionId.current) {
        unsubscribeFromChannel(lastSubscriptionId.current);
        lastSubscriptionId.current = null;
      }
    };
  }, [selectedToken, label, isConnected]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      refreshLiveIfStale(false);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refreshLiveIfStale]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshLiveIfStale(false);
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [refreshLiveIfStale]);

  useSmartInactivityWake(
    useCallback(() => {
      refreshLiveIfStale(true);
    }, [refreshLiveIfStale]),
  );

  return (
    <SnapshotContext.Provider
      value={{
        socketEvent,
        loading,
        setLoading,
        currentSnapshotID,
        getData,
        unsubscribe: (_unused: VolSurfaceToken) => {
          if (lastSubscriptionId.current) {
            unsubscribeFromChannel(lastSubscriptionId.current);
            lastSubscriptionId.current = null;
          }
        },
        type,
        snapshotType,
        setSnapshotType,
        isLiveData,
        handleClickPointVolSeries,
        isConnected,
        currentVols,
        token: selectedToken,
        setToken: (t: VolSurfaceToken) => {
          if (props.tokenKey == null) setToken(t);
        },
      }}
    >
      {props.children}
    </SnapshotContext.Provider>
  );
};
